import { useEffect, useRef, useState } from 'react';
import { BottomSheet } from '../../ui/BottomSheet';
import styles from './Beat.module.css';

const SAVE_MS = 400;

/** The editor lives inside the sheet, so it starts fresh from the saved notes each time the sheet opens. */
function NotesEditor({ notes, onSave }: { notes: string; onSave: (notes: string) => void }) {
  const [text, setText] = useState(notes);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pending = useRef<string | undefined>(undefined);
  const save = useRef(onSave);

  useEffect(() => {
    save.current = onSave;
  });

  useEffect(() => {
    const flush = () => {
      clearTimeout(timer.current);
      if (pending.current !== undefined) {
        save.current(pending.current);
        pending.current = undefined;
      }
    };
    // A phone locking the screen or leaving the page saves too; so does closing the sheet (unmount)
    const onHide = () => flush();
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onHide);
    const t = timer;
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onHide);
      clearTimeout(t.current);
      flush();
    };
  }, []);

  return (
    <div className={styles.sheetBody}>
      <label className="visually-hidden" htmlFor="project-notes">Notes</label>
      <textarea
        id="project-notes"
        className={styles.notes}
        value={text}
        placeholder="Anything to remember about this beat"
        onChange={(e) => {
          setText(e.target.value);
          pending.current = e.target.value;
          clearTimeout(timer.current);
          timer.current = setTimeout(() => {
            if (pending.current !== undefined) save.current(pending.current);
            pending.current = undefined;
          }, SAVE_MS);
        }}
      />
    </div>
  );
}

/** Project notes (SPEC F7): free text, autosaved 400 ms after typing and when the sheet closes (DESIGN_SYSTEM §5). */
export function NotesSheet({ open, notes, onSave, onClose }: { open: boolean; notes: string; onSave: (notes: string) => void; onClose: () => void }) {
  return (
    <BottomSheet open={open} title="Notes" onClose={onClose}>
      <NotesEditor notes={notes} onSave={onSave} />
    </BottomSheet>
  );
}
